import { supabase } from "@/integrations/supabase/client";
import type { AymsEventLocation, EapiClient } from "@/lib/eapi-client";
import { eapiTrailerTypeFromKind } from "../../supabase/functions/_shared/models/trailer/trailer-type.ts";

export type AymsDirection = "in" | "out";

export type AymsSkipReason =
  | "no_eapi_or_tenant"
  | "no_location"
  | "no_org_mapping"
  | "threw";

/**
 * The fixed point AYMS reports for a location: `latitude`/`longitude` when
 * set, else the origin pin (the Locations form writes both from the same
 * input). Returns null when neither is usable.
 */
export function aymsLocationFromRow(
  row: {
    id: string;
    latitude: number | null;
    longitude: number | null;
    origin_latitude: number | null;
    origin_longitude: number | null;
  } | null | undefined,
): AymsEventLocation | null {
  if (!row) return null;
  const lat = row.latitude ?? row.origin_latitude;
  const long = row.longitude ?? row.origin_longitude;
  if (typeof lat !== "number" || typeof long !== "number" || !Number.isFinite(lat) || !Number.isFinite(long)) {
    return null;
  }
  return { id: row.id, lat, long };
}

/**
 * Post a trailer_check_in / trailer_check_out event to EAPI's
 * /v1/ayms/events (ASE-20496).
 *
 * Resolves the most-recently-updated ACTIVE order for `trailerHumanId` in
 * this tenant ("active" = status in ('confirmed','in_transit'); delivered /
 * cancelled trips are excluded so a stale customer_load_id never surfaces
 * as a gate event for a completed trip). When one exists, the event carries
 * its `tender_id` as `customer_load_id` and the customer's Onramp org
 * mapping as `org_id`. When none exists the event has no customer_load_id.
 *
 * `org_id` is still required by EAPI. Until the SCAC → customer mapping
 * lands, a trailer with no active order has no customer to resolve an org
 * from, so the event is skipped with reason `no_org_mapping`.
 *
 * `tractorId` is what AYMS recorded at the gate (the check-in form's truck
 * id); when the caller has none, the active order's assigned tractor is
 * used. `trailer_type` comes from the trailer's own `trailer_types.kind`,
 * mapped through the shared EAPI vocabulary (_shared/models/trailer/trailer-type.ts).
 *
 * Best-effort: any failure returns ok=false with a reason and logs a
 * structured warning. Callers treat this strictly as a side effect.
 */
export async function fireAymsEventForTrailer(params: {
  trailerHumanId: string;
  direction: AymsDirection;
  tenantId: string | null | undefined;
  eapiClient: EapiClient | null;
  /** ATMS `locations.id` of the yard where the gate event happened. */
  locationId: string | null | undefined;
  /** Tractor as recorded at the gate; falls back to the active order's tractor. */
  tractorId?: string | null;
  timestamp?: string;
}): Promise<{ ok: boolean; reason?: AymsSkipReason }> {
  const { trailerHumanId, direction, tenantId, eapiClient, locationId } = params;
  const ts = params.timestamp ?? new Date().toISOString();

  if (!eapiClient || !tenantId) {
    console.warn("[ayms] skipped: missing eapiClient or tenantId", {
      hasClient: !!eapiClient,
      tenantId,
      trailerHumanId,
    });
    return { ok: false, reason: "no_eapi_or_tenant" };
  }

  try {
    const { data: locationRow, error: locErr } = locationId
      ? await supabase
          .from("locations")
          .select("id, latitude, longitude, origin_latitude, origin_longitude")
          .eq("id", locationId)
          .maybeSingle()
      : { data: null, error: null };
    if (locErr) throw locErr;
    const location = aymsLocationFromRow(locationRow);
    if (!location) {
      console.warn("[ayms] skipped: location missing or has no coordinates", {
        locationId,
        tenantId,
        trailerHumanId,
        direction,
      });
      return { ok: false, reason: "no_location" };
    }

    const { data: order, error: orderErr } = await supabase
      .from("orders")
      .select("tender_id, billing_party_id, tractors(name)")
      .eq("tenant_id", tenantId)
      .eq("trailer_id", trailerHumanId)
      .in("status", ["confirmed", "in_transit"])
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (orderErr) throw orderErr;

    let orgId: string | undefined;
    if (order?.billing_party_id) {
      const { data: mapping, error: custErr } = await supabase
        .from("customer_tenant_org_ids")
        .select("onramp_org_id")
        .eq("tenant_id", tenantId)
        .eq("customer_id", order.billing_party_id)
        .maybeSingle();
      if (custErr) throw custErr;
      orgId = mapping?.onramp_org_id ?? undefined;
    }
    if (!orgId) {
      console.warn("[ayms] skipped: no org_id (no active order, or no customer_tenant_org_ids mapping)", {
        tenantId,
        customerId: order?.billing_party_id ?? null,
        trailerHumanId,
        direction,
      });
      return { ok: false, reason: "no_org_mapping" };
    }

    const { data: trailer, error: trailerErr } = await supabase
      .from("trailers")
      .select("trailer_types(kind)")
      .eq("tenant_id", tenantId)
      .eq("trailer_id", trailerHumanId)
      .maybeSingle();
    if (trailerErr) throw trailerErr;
    const trailerType = eapiTrailerTypeFromKind(
      (trailer?.trailer_types as { kind?: string } | null | undefined)?.kind,
    );

    const tractorId =
      params.tractorId?.trim() ||
      (order?.tractors as { name?: string } | null | undefined)?.name?.trim() ||
      undefined;

    await eapiClient.submitAymsEvent({
      event_id: crypto.randomUUID(),
      event_type: direction === "in" ? "trailer_check_in" : "trailer_check_out",
      trailer_id: trailerHumanId,
      ...(trailerType ? { trailer_type: trailerType } : {}),
      ...(tractorId ? { tractor_id: tractorId } : {}),
      location,
      ...(order?.tender_id ? { customer_load_id: order.tender_id } : {}),
      org_id: orgId,
      timestamp: ts,
    });
    return { ok: true };
  } catch (err) {
    console.error("[ayms] submitAymsEvent threw", {
      trailerHumanId,
      tenantId,
      direction,
      err,
    });
    return { ok: false, reason: "threw" };
  }
}
